import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import noop from 'lodash-es/noop';
import styled from 'styled-components';

import Button from './Button';
import PublishOverlay from './PublishOverlay';

import * as navigationActions from '../../common/actions/navigationActions';
import embedActions from '../actions/embedActions';

import styles from '../styles';
import utils from '../../common/utils';
import variables from '../../common/variables';

import { EllipsisMenu, EllipsisMenuItem } from '../../common/styled';
import WarningIcon from '../../common/icons/WarningIcon';
import RoundCheckIcon from '../../common/icons/RoundCheckIcon';
import smalltalk from '../../vendor/smalltalk';

const Unpublished = styled.div`
  color: ${variables.colors.white};
  background: var(--secondaryColor);
  height: 32px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 5px;
  display: flex;
  align-items: center;
`;

const MoveIndicator = ({ onControlsMouseDown, onControlsDoubleClick }) => (
  <div
    style={{
      backgroundImage:
        'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxMCAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgb3BhY2l0eT0iMC4zIj4KPGxpbmUgeDE9IjguNTUwMDUiIHkxPSIwLjQwMjQ2NiIgeDI9IjguNTUwMDUiIHkyPSIxNS40MDI1IiBzdHJva2U9IndoaXRlIi8+CjxsaW5lIHgxPSI0LjU1MDA1IiB5MT0iMC40MDI0NjYiIHgyPSI0LjU1MDA1IiB5Mj0iMTUuNDAyNSIgc3Ryb2tlPSJ3aGl0ZSIvPgo8bGluZSB4MT0iMC41NTAwNDkiIHkxPSIwLjQwMjQ2NiIgeDI9IjAuNTUwMDQ4IiB5Mj0iMTUuNDAyNSIgc3Ryb2tlPSJ3aGl0ZSIvPgo8L2c+Cjwvc3ZnPgo=")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '50% 50%',
      backgroundColor: 'transparent',
      minWidth: '27px',
      height: '100%',
      marginRight: '5px',
      cursor: 'move',
    }}
    onMouseDown={e => {
      onControlsMouseDown(e);
    }}
    onDoubleClick={onControlsDoubleClick}
  />
);

MoveIndicator.propTypes = {
  onControlsMouseDown: PropTypes.func.isRequired,
  onControlsDoubleClick: PropTypes.func.isRequired,
};

let moveProcess = false;
let moveProcessPosition = {
  x: undefined,
  y: undefined,
};

const TopControls = props => {
  const bbox = useRef(null);
  const topControlsRef = useRef(null);
  const ellipsisMenu = useRef(null);
  const ellipsisButton = useRef(null);
  const title = props.title; // titleMeta && titleMeta.getAttribute('content');
  const [visibleEllipsisMenu, setVisibleEllipsisMenu] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState(() => ({ x: null, y: 11 }));

  const [isPublishOverlayVisible, setIsPublishOverlayVisible] = useState(false);

  useEffect(() => {
    document.title = props.title;
  }, [props.title]);

  useEffect(() => {
    bbox.current = topControlsRef.current.getBoundingClientRect();
  }, [props]);

  const onWindowMouseUp = e => {
    setDragging(false);
    moveProcess = false;
    moveProcessPosition = {
      x: undefined,
      y: undefined,
    };
  };

  useEffect(() => {
    if (!props.visible) {
      setIsPublishOverlayVisible(false);
    }
  }, [props.visible]);

  const checkPosition = (e, x, y) => {
    if (bbox.current && (position.x !== null || x)) {
      const _x = x || position.x;
      const _y = y || position.y;
      const maxLeft = window.innerWidth - bbox.current.width;
      const maxTop = window.innerHeight - bbox.current.height;
      setPosition({
        x: Math.max(Math.min(_x, maxLeft), 0),
        y: Math.max(Math.min(_y, maxTop), 0),
      });
    }
  };

  const onWindowMouseMove = e => {
    if (moveProcess) {
      const pageX = e.pageX - moveProcessPosition.x;
      const pageY = e.pageY - moveProcessPosition.y;
      checkPosition(e, pageX, pageY);
    }
  };

  const handleClickOutside = e => {
    if (
      ellipsisMenu &&
      visibleEllipsisMenu &&
      !ellipsisMenu.current.contains(e.target)
    ) {
      setVisibleEllipsisMenu(false);
    }
  };

  const addListeners = () => {
    document.addEventListener('mousedown', handleClickOutside, true);
    window.addEventListener('mouseup', onWindowMouseUp, false);
    document.addEventListener('mousemove', onWindowMouseMove, false);
    window.addEventListener('resize', checkPosition);
  };

  const removeListeners = () => {
    document.removeEventListener('mousedown', handleClickOutside, true);
    document.removeEventListener('mousemove', onWindowMouseMove);
    window.removeEventListener('mouseup', onWindowMouseUp);
    window.removeEventListener('resize', checkPosition);
  };

  useEffect(() => {
    addListeners();
    return () => {
      removeListeners();
    };
  }, [addListeners, removeListeners]);

  const onControlsDoubleClick = e => {
    setPosition({
      x: null,
      y: 11,
    });
  };
  const onControlsMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    const elRect = el.getBoundingClientRect();
    setDragging(true);
    moveProcess = true;
    moveProcessPosition = {
      x: e.pageX - elRect.x,
      y: e.pageY - elRect.y,
    };
  };

  const goToPreviousSlide = () => {
    props.actions.dispatchPreviousSlide();
  };

  const goToNextSlide = () => {
    props.actions.dispatchNextSlide();
  };

  const edit = () => {
    props.actions.openEditor(window.presentationId, props.index + 1, true);
  };

  const toggleVideoRecorder = () => {
    setVisibleEllipsisMenu(false);
    if (props.isPreview) {
      smalltalk.alert(
        'Sorry',
        'To use the video speaker, please go to the published version of this presentation',
      );
    }
    if (props.visibleVideoRecorder) {
      props.actions.hideVideoRecorder();
    } else {
      props.actions.showVideoRecorder();
    }
  };

  const togglePublishOverlay = () => {
    setIsPublishOverlayVisible(!isPublishOverlayVisible);
  };

  const toggleVideoViewer = () => {
    setVisibleEllipsisMenu(false);
    if (props.visibleVideoViewer) {
      props.actions.hideVideoViewer();
    } else {
      props.actions.showVideoViewer();
    }
  };

  const toggleEllipsisMenu = () => {
    setVisibleEllipsisMenu(!visibleEllipsisMenu);
  };

  const clickClone = () => {
    setVisibleEllipsisMenu(false);
    props.actions.openPresentationClone();
  };

  const clickDownload = () => {
    setVisibleEllipsisMenu(false);
    props.actions.openPresentationPDF();
  };

  const clickShare = () => {
    setVisibleEllipsisMenu(false);
    props.clickShare();
  };

  const clickSpeakerView = () => {
    setVisibleEllipsisMenu(false);
    props.onOpenSpeakerView();
  };

  const clickGoToPresentation = () => {
    setVisibleEllipsisMenu(false);
    props.actions.visitViewer(window.presentationId, props.index + 1, true);
  };

  const clickGoToDashboard = e => {
    setVisibleEllipsisMenu(false);
    props.actions.visitDashboard(e.metaKey || e.ctrlKey);
  };

  const clickReportPresentation = e => {
    window.open(
      `mailto:${encodeURIComponent(
        'info@ludus.one',
      )}?subject=${encodeURIComponent(
        `I want to report the presentation "${window.presentationId}"`,
      )}&body=${encodeURIComponent(
        `ID: ${window.presentationId}\n\n—\n\nWhy do you want to report this presentation?\n\n...`,
      )}`,
      '_blank',
    );
  };

  const clickGoToLudus = () => {
    setVisibleEllipsisMenu(false);
    props.actions.goToHome();
  };

  const [controlsStyles, setControlStyles] = useState({ display: 'none' });

  const isEmbed = utils.isEmbed();
  const isMobileDevice = utils.isMobileDevice();

  useEffect(() => {
    if (isEmbed || isMobileDevice) {
      setControlStyles({
        ...styles.controls,
        marginTop: props.visible ? 0 : '-38px',
        transitionDelay: props.visible ? '0s, 0s' : '0s, .2s',
        position: isEmbed ? 'fixed' : 'absolute',
      });
    } else {
      setControlStyles({
        ...styles.controlsCentered,
        position: 'fixed',
        opacity: props.visible && topControlsRef.current ? 1 : 0,
        pointerEvents: props.visible ? 'auto' : 'none',
        transitionDelay: props.visible ? '0s, 0s' : '0s, .2s',
        top: position.y,
        transform: position.x === null ? 'translate(-50%,0)' : 'translate(0,0)',
        left: position.x === null ? '50%' : position.x,
        zIndex: '999999',
      });
    }
  }, [position, props.visible, topControlsRef.current]);

  const controlsListStyles = {
    ...styles.controlsBase,
    ...styles.controlsList,
  };

  const slideCounterInnerStyles = {
    ...styles.slideCounterInner,
  };

  const shouldShowArrowButtons = !isMobileDevice;
  const shouldShowFullScreenButton = utils.fullscreenEnabled();
  const shouldShowSpeakerViewButton =
    !props.isOffline && window.isEditable && !isMobileDevice;
  const shouldShowEditButton = window.isEditable && !isMobileDevice;
  const shouldShowToggleVideoRecorderButton =
    !props.isOffline && window.isEditable && !isMobileDevice;
  const shouldShowToggleVideoViewerButton =
    !props.isOffline && !window.isEditable && props.presentationHasMedia;
  const shouldShowCloneButton = !props.isOffline && props.canClone;
  const shouldShowDownloadButton = !props.isOffline && props.canDownload;
  const shouldShowShareButton = !props.isOffline;
  const shouldShowUnpublishButton =
    !props.isOffline && !props.visiblePresentation && window.isEditable;
  const shouldShowPublishActions = !props.isOffline && window.isEditable;
  const shouldShowGoToPresentationButton = isEmbed;
  const shouldShowGoToDashboardButton =
    !shouldShowGoToPresentationButton && window.isEditable;
  const shouldShowReportPresentationButton = true;
  const shouldShowGoToLudusButton =
    !shouldShowGoToDashboardButton && !window.isCustomDomain;

  const shouldShowEllipsisButton =
    shouldShowCloneButton ||
    shouldShowDownloadButton ||
    shouldShowGoToPresentationButton ||
    shouldShowGoToDashboardButton ||
    shouldShowReportPresentationButton ||
    shouldShowGoToLudusButton;

  const minDigits = Math.max(2, utils.charLength(props.totalSlides));
  const currentSlide = utils.leftPad(props.currentSlide, minDigits);
  const totalSlides = utils.leftPad(props.totalSlides, minDigits);

  return (
    <>
      <div
        style={{
          // BLOCK OTHER MOUSE EVENTS WHILE DRAGGING
          display: dragging ? 'block' : 'none',
          position: 'absolute',
          width: '100%',
          height: '100%',
          zIndex: 8888,
        }}
      />

      <div
        style={controlsStyles}
        ref={topControlsRef}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            maxWidth: '50vw',
            cursor: 'auto',
          }}
        >
          {!isEmbed && !isMobileDevice ? (
            <MoveIndicator
              onControlsDoubleClick={onControlsDoubleClick}
              onControlsMouseDown={onControlsMouseDown}
            />
          ) : (
            <div
              style={{
                width: '18px',
                height: '100%',
              }}
            />
          )}
          <span style={styles.title}>
            {shouldShowPublishActions && (
              <span style={styles.status}>
                {props.isPreview ? 'Draft / ' : ''}
              </span>
            )}{' '}
            {title}
          </span>
          {shouldShowUnpublishButton && <Unpublished>Private</Unpublished>}
        </div>

        <ul style={controlsListStyles} className="bf">
          {shouldShowPublishActions && (
            <li style={styles.buttonWrapper}>
              <Button onPress={togglePublishOverlay} icon="">
                {props.hasChangesToPublish ? (
                  <WarningIcon
                    height={14}
                    color="#FF6562"
                    style={{
                      margin: '0 8px',
                      position: 'relative',
                      top: '-1px',
                    }}
                  />
                ) : (
                  <RoundCheckIcon
                    height={16}
                    color={variables.colors.darkerTextGrey}
                    style={{
                      margin: '0 8px',
                      position: 'relative',
                      top: '-1px',
                    }}
                  />
                )}
                Publish
              </Button>
            </li>
          )}

          {shouldShowArrowButtons && (
            <li style={styles.buttonWrapper}>
              <Button
                icon="arrow-prev"
                onPress={goToPreviousSlide}
                disabled={!props.canGoPrevious}
              />
            </li>
          )}
          <li style={styles.buttonWrapper}>
            <div style={slideCounterInnerStyles}>
              <span id="current-slide" style={styles.slideCounterCurrentSlide}>
                {currentSlide}
              </span>
              <span style={styles.slideCounterSeparator}>/</span>
              <span id="total-slides" style={styles.slideCounterTotalSlides}>
                {totalSlides}
              </span>
            </div>
          </li>
          {shouldShowArrowButtons && (
            <li style={styles.buttonWrapper}>
              <Button
                icon="arrow-next"
                onPress={goToNextSlide}
                disabled={!props.canGoNext}
              />
            </li>
          )}
          {shouldShowEditButton && (
            <li style={styles.buttonWrapper}>
              <Button onPress={edit} icon="edit" />
            </li>
          )}
          {shouldShowFullScreenButton && (
            <li style={styles.buttonWrapper}>
              {props.isFullScreen ? (
                <Button onPress={utils.exitFullScreen} icon="exitFullScreen" />
              ) : (
                <Button onPress={utils.requestFullscreen} icon="fullScreen" />
              )}
            </li>
          )}
          {shouldShowShareButton && (
            <li style={styles.buttonWrapper}>
              <Button onPress={clickShare} icon="shiraz" />
            </li>
          )}

          {shouldShowEllipsisButton && (
            <li style={styles.buttonWrapper}>
              <div
                style={{
                  position: 'relative',
                }}
                onTouchMove={utils.preventDefault}
              >
                <Button
                  icon="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNCIgaGVpZ2h0PSIxOCIgdmlld0JveD0iMCAwIDQgMTgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxjaXJjbGUgY3g9IjIuMjkyOTciIGN5PSIxLjkwMjQ3IiByPSIxLjUiIGZpbGw9IndoaXRlIi8+CjxjaXJjbGUgY3g9IjIuMjkyOTciIGN5PSI4LjkwMjQ3IiByPSIxLjUiIGZpbGw9IndoaXRlIi8+CjxjaXJjbGUgY3g9IjIuMjkyOTciIGN5PSIxNS45MDI1IiByPSIxLjUiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo="
                  onPress={toggleEllipsisMenu}
                  iconType="image"
                  iconWidth="27px"
                  iconHeight="17px"
                  ref={ellipsisButton}
                />
                <EllipsisMenu
                  visible={visibleEllipsisMenu}
                  style={{
                    left: 'auto',
                    right: '0',
                    marginTop: '5px',
                  }}
                  ref={ellipsisMenu}
                  positionTop="36px"
                >
                  {shouldShowCloneButton && (
                    <EllipsisMenuItem onClick={clickClone}>
                      <span>Clone</span>
                      <i className="icon-duplicae-viewer" />
                    </EllipsisMenuItem>
                  )}
                  {shouldShowDownloadButton && (
                    <EllipsisMenuItem onClick={clickDownload}>
                      <span>Download PDF</span>
                      <i className="icon-download-doc" />
                    </EllipsisMenuItem>
                  )}
                  {shouldShowToggleVideoRecorderButton && (
                    <EllipsisMenuItem onClick={toggleVideoRecorder}>
                      <span>
                        {!props.isPreview && props.visibleVideoRecorder
                          ? 'Hide speaker video'
                          : 'Show speaker video'}
                      </span>
                      <i
                        className={
                          props.visibleVideoRecorder
                            ? 'icon-vox-show'
                            : 'icon-vox-hide'
                        }
                      />
                    </EllipsisMenuItem>
                  )}
                  {shouldShowToggleVideoViewerButton && (
                    <EllipsisMenuItem onClick={toggleVideoViewer}>
                      <span>
                        {!props.isPreview && props.visibleVideoViewer
                          ? 'Hide speaker video'
                          : 'Show speaker video'}
                      </span>
                      <i
                        className={
                          props.visibleVideoViewer
                            ? 'icon-vox-show'
                            : 'icon-vox-hide'
                        }
                      />
                    </EllipsisMenuItem>
                  )}
                  {shouldShowSpeakerViewButton && (
                    <EllipsisMenuItem
                      onClick={clickSpeakerView}
                      disabled={props.isOpenSpeakerView}
                    >
                      <span>Open speaker view</span>
                      <i className="icon-speaker-view" />
                    </EllipsisMenuItem>
                  )}
                  {shouldShowGoToDashboardButton && (
                    <EllipsisMenuItem onClick={clickGoToDashboard}>
                      <span>Go to dashboard</span>
                      <img src="/assets/img/grid.svg" alt="" />
                    </EllipsisMenuItem>
                  )}
                  {shouldShowGoToPresentationButton && (
                    <EllipsisMenuItem onClick={clickGoToPresentation}>
                      <span>Go to presentation</span>
                      <i className="icon-popup" />
                    </EllipsisMenuItem>
                  )}
                  {shouldShowReportPresentationButton && (
                    <EllipsisMenuItem onClick={clickReportPresentation}>
                      <span>Report</span>
                      <img src="/assets/img/flag.svg" alt="" />
                    </EllipsisMenuItem>
                  )}
                  {shouldShowGoToLudusButton && (
                    <EllipsisMenuItem onClick={clickGoToLudus}>
                      <span>About Ludus</span>
                      <i className="icon-help_icn" />
                    </EllipsisMenuItem>
                  )}
                </EllipsisMenu>
              </div>
            </li>
          )}
        </ul>
        {shouldShowPublishActions && (
          <PublishOverlay
            visible={isPublishOverlayVisible}
            style={{ position: 'absolute', top: '45px' }}
          />
        )}
      </div>
    </>
  );
};

TopControls.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  isOffline: PropTypes.bool.isRequired,
  currentSlide: PropTypes.number.isRequired,
  totalSlides: PropTypes.number.isRequired,
  isFullScreen: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  canGoPrevious: PropTypes.bool.isRequired,
  canGoNext: PropTypes.bool.isRequired,
  visible: PropTypes.bool.isRequired,
  presentationHasMedia: PropTypes.bool.isRequired,
  visibleVideoRecorder: PropTypes.bool.isRequired,
  visibleVideoViewer: PropTypes.bool.isRequired,
  canClone: PropTypes.bool.isRequired,
  canDownload: PropTypes.bool.isRequired,
  visiblePresentation: PropTypes.bool.isRequired,
  onOpenSpeakerView: PropTypes.func.isRequired,
  isOpenSpeakerView: PropTypes.bool.isRequired,
  clickShare: PropTypes.func.isRequired,
  isPreview: PropTypes.bool.isRequired,
  hasChangesToPublish: PropTypes.bool.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  title: PropTypes.string.isRequired,
};

TopControls.defaultProps = {
  onMouseEnter: noop,
  onMouseLeave: noop,
};

const mapStateToProps = state => ({
  isOffline: state.appReducer.isOffline,
  currentSlide: state.slideReducer.index + 1,
  totalSlides: state.slideReducer.slides.length,
  isFullScreen: state.fullScreenReducer,
  index: state.slideReducer.index,
  canGoPrevious: state.slideReducer.index > 0,
  canGoNext: state.slideReducer.index < state.slideReducer.slides.length - 1,
  visibleVideoRecorder: state.mediaRecorderReducer.visibleVideo,
  visibleVideoViewer: state.mediaViewerReducer.visibleVideo,
  canClone: state.slideReducer.canClone,
  canDownload: state.slideReducer.canDownload,
  visiblePresentation: state.slideReducer.visible,
  title: state.slideReducer.title,
  isPreview: Boolean(
    new URLSearchParams(window.location.search).get('draft') === '1',
  ),
  hasChangesToPublish: state.slideReducer.hasChangesToPublish,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...navigationActions,
      ...embedActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(React.memo(TopControls));
